import React, { useState } from "react";
import {
  employeeConflictKind,
  periodConflictKind,
} from "./vacationConflicts";

const MONTHS = [
  "Янв", "Фев", "Мар", "Апр", "Май", "Июн",
  "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек",
];

const MONTHS_FULL = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const UNPAID_COLOR = "#8b5cf6";

const STATUS_COLORS = {
  active: "#35c79a",
  planned: "#5c87b0",
  overdue: "#7e8a9a",
};

const CONFLICT_COLORS = {
  existing: "#f59e0b",
  new: "#ef4444",
};

function formatDate(d) {
  if (!d) return "";
  const date = new Date(d + "T00:00:00");
  return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function formatDateFull(d) {
  if (!d) return "";
  const date = new Date(d + "T00:00:00");
  return date.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
}

function formatDateInput(d) {
  if (!d) return "";
  if (typeof d === "string" && d.match(/^\d{4}-\d{2}-\d{2}$/)) return d;
  const date = new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDayOfYearIndex(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr + "T00:00:00");
  const start = new Date(date.getFullYear(), 0, 1);
  const diff = date - start;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function todayAtMidnight() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

function getPeriodStatus(period) {
  if (!period.start || !period.end) return null;
  const now = todayAtMidnight();
  const start = new Date(period.start + "T00:00:00");
  const end = new Date(period.end + "T00:00:00");

  if (now >= start && now <= end) return "active";
  if (now > end) return "overdue";
  return "planned";
}

function getVacationColor(period) {
  const status = getPeriodStatus(period);
  if (status === "active") return "#35c79a";
  if (status === "overdue") return "#7e8a9a";
  return "#5c87b0";
}

function onKeyActivate(e, fn) {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    fn();
  }
}

export function rangeStyle(start, end, sheetYear) {
  if (!start || !end || sheetYear == null || sheetYear === "") return null;
  const year = Number(sheetYear);
  if (!Number.isFinite(year)) return null;
  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  const daysInYear = isLeap ? 366 : 365;

  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  const clampedStart = startDate < yearStart ? yearStart : startDate;
  const clampedEnd = endDate > yearEnd ? yearEnd : endDate;
  if (clampedStart > clampedEnd) return null;
  const startDayOfYear = getDayOfYearIndex(formatDateInput(clampedStart));
  const endDayOfYear = getDayOfYearIndex(formatDateInput(clampedEnd));

  return {
    left: `${(startDayOfYear / daysInYear) * 100}%`,
    width: `${((endDayOfYear - startDayOfYear + 1) / daysInYear) * 100}%`,
  };
}

export default function VacationYearTimeline({
  employees = [],
  year,
  selectedMonth,
  onMonthSelect,
  editMode = false,
  displayConflicts = [],
  freshConflicts = [],
}) {
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  return (
    <div className="data-card" style={{ overflowX: "auto", marginBottom: 24 }}>
      <div className="data-card-header">
        <div className="data-card-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          Календарь отпусков
          {selectedMonth !== null && selectedMonth !== undefined && (
            <span style={{ marginLeft: 8, fontSize: 13, color: "var(--text-muted)", fontWeight: 400 }}>
              — {MONTHS_FULL[selectedMonth]}
            </span>
          )}
        </div>
      </div>
      <div className="data-card-body" style={{ padding: 0 }}>
        <div className="vacation-timeline">
          <div className="timeline-header">
            <div className="timeline-employee-col">Сотрудник</div>
            <div className="timeline-days-col">Дней</div>
            <div className="timeline-remaining-col">Остаток</div>
            <div className="timeline-months">
              {MONTHS.map((m, i) => (
                <div
                  key={i}
                  className={`timeline-month ${selectedMonth === i ? "active" : ""}`}
                  onClick={() => onMonthSelect?.(i)}
                  onKeyDown={(e) => onKeyActivate(e, () => onMonthSelect?.(i))}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedMonth === i}
                  aria-label={`Фильтр по месяцу: ${MONTHS_FULL[i]}`}
                  style={{ cursor: "pointer" }}
                >
                  {m}
                </div>
              ))}
            </div>
            {editMode && <div className="timeline-actions-col" />}
          </div>

          <div className="timeline-body">
            {employees.map((emp, idx) => {
              const usedDays = emp.periods.reduce((s, p) => s + (p.days || 0), 0);
              const progress = emp.total_days ? (usedDays / emp.total_days) * 100 : 0;
              const isExpanded = expandedEmployee === idx;
              const rowConflictKind = employeeConflictKind(
                emp.employee_id,
                displayConflicts,
                editMode ? freshConflicts : []
              );

              return (
                <React.Fragment key={emp.employee_id ?? idx}>
                  <div
                    className={`timeline-row${rowConflictKind ? ` timeline-row--conflict-${rowConflictKind}` : ""}`}
                    onClick={() => !editMode && setExpandedEmployee(isExpanded ? null : idx)}
                    onKeyDown={(e) => !editMode && onKeyActivate(e, () => setExpandedEmployee(isExpanded ? null : idx))}
                    role={!editMode ? "button" : undefined}
                    tabIndex={!editMode ? 0 : undefined}
                    aria-expanded={!editMode ? isExpanded : undefined}
                    style={{ cursor: !editMode ? "pointer" : "default" }}
                  >
                    <div className="timeline-employee-col">
                      <div className="employee-cell">
                        <div className="employee-avatar" style={{
                          background: emp.periods.some((p) => getPeriodStatus(p) === "active")
                            ? "linear-gradient(135deg, #10b981, #059669)"
                            : undefined,
                        }}>
                          {emp.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </div>
                        <div className="employee-info">
                          <span className="employee-name">{emp.name}</span>
                          <div className="progress-container" style={{ marginTop: 4 }}>
                            <div className="progress-bar" style={{ height: 4, flex: "none", width: 60 }}>
                              <div className="progress-fill" style={{ width: `${Math.min(progress, 100)}%` }} />
                            </div>
                            <span className="progress-text" style={{ fontSize: 11 }}>
                              {usedDays}/{emp.total_days || "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="timeline-days-col">
                      <span className="vacation-days-badge" style={{
                        background: usedDays > 0
                          ? "var(--accent-gradient)"
                          : "var(--bg-glass)",
                        color: usedDays > 0 ? "#fff" : "var(--text-muted)",
                      }}>
                        {usedDays}
                      </span>
                    </div>

                    <div className="timeline-remaining-col">
                      <span className={emp.remaining !== null && emp.remaining < 0 ? "text-danger" : ""} style={{
                        color: emp.remaining !== null && emp.remaining < 7 ? "#f59e0b" : undefined,
                        fontWeight: emp.remaining !== null && emp.remaining < 7 ? 700 : 600,
                      }}>
                        {emp.remaining ?? "—"}
                      </span>
                    </div>

                    <div className="timeline-months">
                      {MONTHS.map((_, monthIdx) => (
                        <div
                          key={monthIdx}
                          className="timeline-month-cell"
                        />
                      ))}

                      {emp.periods.map((p, pIdx) => {
                        if (!p.start || !p.end) return null;
                        const blockConflictKind = periodConflictKind(
                          emp.employee_id,
                          p,
                          displayConflicts,
                          editMode ? freshConflicts : []
                        );
                        const pos = rangeStyle(p.start, p.end, year);
                        if (!pos) return null;

                        return (
                          <div
                            key={`vac-${pIdx}`}
                            className="vacation-block-absolute"
                            style={{
                              ...pos,
                              backgroundColor: blockConflictKind
                                ? CONFLICT_COLORS[blockConflictKind]
                                : getVacationColor(p),
                            }}
                            title={`${formatDate(p.start)} — ${formatDate(p.end)} (${p.days} дн.)${
                              blockConflictKind
                                ? blockConflictKind === "new"
                                  ? " · новое пересечение у клиента"
                                  : " · пересечение у клиента"
                                : ""
                            }`}
                          />
                        );
                      })}

                      {(emp.unpaid_leaves || []).map((leave, leaveIdx) => {
                        const pos = rangeStyle(leave.start, leave.end, year);
                        if (!pos) return null;
                        return (
                          <div
                            key={`unpaid-${leave.id ?? leaveIdx}`}
                            className="vacation-block-absolute vacation-block-absolute--unpaid"
                            style={{
                              ...pos,
                              backgroundColor: UNPAID_COLOR,
                            }}
                            title={`Отгул ${formatDate(leave.start)} — ${formatDate(leave.end)}`}
                          />
                        );
                      })}
                    </div>

                    {editMode && <div className="timeline-actions-col" />}
                  </div>

                  {isExpanded && !editMode && (
                    <div className="timeline-expanded">
                      <div className="timeline-expanded-content">
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16 }}>
                          {emp.periods.map((p, pIdx) => {
                            const status = getPeriodStatus(p);
                            const statusLabel = {
                              active: "В отпуске",
                              planned: "Запланирован",
                              overdue: "Завершен",
                            }[status] || "";

                            const used = status === "overdue";
                            return (
                              <div key={pIdx} className="period-card" style={{
                                borderLeft: `3px solid ${getVacationColor(p)}`,
                                opacity: used ? 0.7 : 1,
                              }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                  <span style={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    textDecoration: used ? "line-through" : "none",
                                    color: used ? "var(--text-muted)" : undefined,
                                  }}>
                                    Период {pIdx + 1}
                                  </span>
                                  {status && (
                                    <span style={{
                                      fontSize: 11,
                                      padding: "2px 8px",
                                      borderRadius: 10,
                                      background: `${STATUS_COLORS[status]}20`,
                                      color: STATUS_COLORS[status],
                                      fontWeight: 600,
                                    }}>
                                      {used ? "Использован" : statusLabel}
                                    </span>
                                  )}
                                </div>
                                <div style={{
                                  fontSize: 13,
                                  color: "var(--text-secondary)",
                                  lineHeight: 1.6,
                                  textDecoration: used ? "line-through" : "none",
                                }}>
                                  <div><strong>Начало:</strong> {formatDateFull(p.start)}</div>
                                  <div><strong>Окончание:</strong> {formatDateFull(p.end)}</div>
                                  <div><strong>Длительность:</strong> {p.days} дн.</div>
                                  {p.application_date && (
                                    <div><strong>Заявление:</strong> {formatDateFull(p.application_date)}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {emp.periods.length === 0 && (
                            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
                              Нет запланированных отпусков
                            </div>
                          )}
                        </div>

                        {emp.hire_date && (
                          <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-muted)" }}>
                            Принят на работу: {formatDateFull(emp.hire_date)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .vacation-timeline {
          min-width: 980px;
        }
        .timeline-header {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-color);
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0;
          color: var(--text-muted);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .timeline-body {
          max-height: 720px;
          overflow-y: auto;
        }
        .timeline-row {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-color);
          transition: background-color var(--transition-fast);
        }
        .timeline-row:hover {
          background: var(--bg-glass);
        }
        .timeline-expanded {
          background: var(--bg-glass);
          border-bottom: 1px solid var(--border-color);
        }
        .timeline-expanded-content {
          padding: 14px 14px 14px 292px;
        }
        .period-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: 12px;
        }
        .timeline-employee-col {
          width: 260px;
          flex-shrink: 0;
          position: sticky;
          left: 0;
          z-index: 3;
          background: var(--bg-card);
        }
        .timeline-header .timeline-employee-col {
          background: var(--bg-secondary);
        }
        .timeline-days-col {
          width: 60px;
          flex-shrink: 0;
          text-align: center;
        }
        .timeline-remaining-col {
          width: 70px;
          flex-shrink: 0;
          text-align: center;
          font-weight: 600;
        }
        .timeline-actions-col {
          width: 80px;
          flex-shrink: 0;
          text-align: center;
        }
        .timeline-months {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 2px;
          position: relative;
          height: 30px;
        }
        .timeline-month {
          text-align: center;
          padding: 4px;
          border-radius: 6px;
          transition: background-color var(--transition-fast);
        }
        .timeline-month:hover {
          background: var(--bg-glass-hover);
        }
        .timeline-month.active {
          background: var(--accent-primary);
          color: #fff;
        }
        .timeline-month-cell {
          height: 30px;
          position: relative;
          background: var(--bg-glass);
          border-radius: 4px;
        }
        .vacation-block-absolute {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          height: 18px;
          border-radius: 4px;
          opacity: 0.9;
          cursor: pointer;
          z-index: 2;
        }
        .vacation-block-absolute--unpaid {
          z-index: 3;
        }
        .vacation-block-absolute:hover {
          opacity: 1;
          z-index: 10;
          outline: 2px solid rgba(255,255,255,0.35);
        }
        .vacation-days-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 12px;
        }
        .text-danger {
          color: var(--score-low);
          font-weight: 700;
        }
        .timeline-row--conflict-existing {
          box-shadow: inset 3px 0 0 #f59e0b;
          background: rgba(245, 158, 11, 0.08);
        }
        .timeline-row--conflict-new {
          box-shadow: inset 3px 0 0 #ef4444;
          background: rgba(239, 68, 68, 0.08);
        }
        @media (max-width: 1024px) {
          .vacation-timeline {
            min-width: 820px;
          }
          .timeline-employee-col {
            width: 220px;
          }
          .timeline-expanded-content {
            padding-left: 236px;
          }
        }
        @media (max-width: 768px) {
          .vacation-timeline {
            min-width: 820px;
          }
          .timeline-expanded-content {
            padding-left: 16px;
          }
          .timeline-body {
            max-height: none;
          }
        }
      `}</style>
    </div>
  );
}
